import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useGitpodAPI } from "@/hooks/use-gitpod-api";
import { useAuthenticatedUser } from "@/queries/user-queries";
import type { PlainMessage } from "@bufbuild/protobuf";
import {
    CreateIntegrationRequest,
    UpdateIntegrationRequest,
    DeleteIntegrationRequest,
    ValidateIntegrationRequest,
    type Integration,
} from "gitpod-next-api/gitpod/v1/integration_pb";
import { useMemo } from "react";

// Query key factories
export const integrationDefinitionsQueryKey = () => ["integration-definitions"];
export const integrationsQueryKey = (organizationId?: string) => ["integrations", organizationId].filter(Boolean);
export const integrationQueryKey = (integrationId: string) => ["integration", integrationId];
export const integrationValidationQueryKey = (integrationId: string) => ["integration-validation", integrationId];

// List integration definitions
export const useListIntegrationDefinitions = () => {
    const api = useGitpodAPI();
    return useQuery({
        queryKey: integrationDefinitionsQueryKey(),
        queryFn: async () => {
            const response = await api.integrationService.listIntegrationDefinitions({});
            return response.definitions;
        },
    });
};

// List integrations for organization
export const useListIntegrations = (organizationId?: string) => {
    const api = useGitpodAPI();
    return useQuery({
        queryKey: integrationsQueryKey(organizationId),
        queryFn: async () => {
            if (!organizationId) {
                return [];
            }
            const response = await api.integrationService.listIntegrations({});
            return response.integrations;
        },
        enabled: !!organizationId,
    });
};

// Get a single integration by ID
export const useGetIntegration = (integrationId?: string) => {
    const api = useGitpodAPI();
    return useQuery({
        queryKey: integrationQueryKey(integrationId ?? ""),
        queryFn: async () => {
            const response = await api.integrationService.getIntegration({ id: integrationId });
            return response.integration;
        },
        enabled: !!integrationId,
    });
};

// Validate integration configuration
export const useValidateIntegration = (integrationId?: string) => {
    const api = useGitpodAPI();
    return useQuery({
        queryKey: integrationValidationQueryKey(integrationId ?? ""),
        queryFn: async () => {
            const response = await api.integrationService.validateIntegration(
                new ValidateIntegrationRequest({ integrationId }),
            );
            return response;
        },
        enabled: !!integrationId,
        // Cache for 5 minutes to avoid hitting the provider API on every
        // tab focus. The query cache is in-memory, so a full page reload
        // always fetches fresh.
        staleTime: 5 * 60 * 1000,
    });
};


// Create integration mutation
export const useCreateIntegration = () => {
    const api = useGitpodAPI();
    const queryClient = useQueryClient();
    const { data: user } = useAuthenticatedUser();

    return useMutation({
        mutationFn: async (request: PlainMessage<CreateIntegrationRequest>) => {
            const response = await api.integrationService.createIntegration(new CreateIntegrationRequest(request));
            return response.integration;
        },
        onSuccess: () => {
            // Invalidate integrations list
            void queryClient.invalidateQueries({
                queryKey: integrationsQueryKey(user?.organizationId),
            });
        },
    });
};

// Update integration mutation
export const useUpdateIntegration = () => {
    const api = useGitpodAPI();
    const queryClient = useQueryClient();
    const { data: user } = useAuthenticatedUser();

    return useMutation({
        mutationFn: async (request: PlainMessage<UpdateIntegrationRequest>) => {
            const response = await api.integrationService.updateIntegration(new UpdateIntegrationRequest(request));
            return response.integration;
        },
        onSuccess: () => {
            // Invalidate integrations list
            void queryClient.invalidateQueries({
                queryKey: integrationsQueryKey(user?.organizationId),
            });
        },
    });
};

// Delete integration mutation
export const useDeleteIntegration = () => {
    const api = useGitpodAPI();
    const queryClient = useQueryClient();
    const { data: user } = useAuthenticatedUser();

    return useMutation({
        mutationFn: async (request: PlainMessage<DeleteIntegrationRequest>) => {
            await api.integrationService.deleteIntegration(new DeleteIntegrationRequest(request));
        },
        onSuccess: () => {
            // Invalidate integrations list
            void queryClient.invalidateQueries({
                queryKey: integrationsQueryKey(user?.organizationId),
            });
        },
    });
};

export type MCPIntegration = PlainMessage<Integration> & {
    name: string;
    iconUrl?: string;
    description?: string;
};

export const useListMCPIntegrations = (organizationId?: string) => {
    const { data: integrations, isLoading: integrationsLoading } = useListIntegrations(organizationId);
    const { data: definitions, isLoading: definitionsLoading } = useListIntegrationDefinitions();

    return useMemo(() => {
        if (integrationsLoading || definitionsLoading) {
            return { data: [], isLoading: true };
        }

        // Filter for enabled integrations with MCP capability and merge with definitions
        const mcpIntegrations: MCPIntegration[] =
            integrations
                ?.filter((i) => i.enabled && i.capabilities?.mcp)
                .map((integration) => {
                    const definition = definitions?.find((d) => d.id === integration.integrationDefinitionId);
                    return {
                        ...integration,
                        name: definition?.name || "Unknown",
                        iconUrl: definition?.iconUrl,
                        description: definition?.description,
                    };
                }) || [];

        return { data: mcpIntegrations, isLoading: false };
    }, [integrations, definitions, integrationsLoading, definitionsLoading]);
};
